import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import appConfig from "../../../../config/appConfig";
import {
  getToken,
  getUserPermissions,
  getUserRole,
  refreshSessionAndPermissions,
} from "../../../../shared/lib/auth/authHelper";
import styles from "./PermissionsPage.module.css";

function authHeaders() {
  const token = getToken();
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function apiGet(path) {
  const res = await fetch(`${appConfig.API_BASE_URL}${path}`, {
    method: "GET",
    headers: authHeaders(),
    credentials: "include",
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `GET ${path} failed`);
  }

  return res.json();
}

async function apiPost(path, body) {
  const res = await fetch(`${appConfig.API_BASE_URL}${path}`, {
    method: "POST",
    headers: authHeaders(),
    credentials: "include",
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `POST ${path} failed`);
  }

  return res.json();
}

async function apiPut(path, body) {
  const res = await fetch(`${appConfig.API_BASE_URL}${path}`, {
    method: "PUT",
    headers: authHeaders(),
    credentials: "include",
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `PUT ${path} failed`);
  }

  return res.json();
}

async function apiDelete(path) {
  const res = await fetch(`${appConfig.API_BASE_URL}${path}`, {
    method: "DELETE",
    headers: authHeaders(),
    credentials: "include",
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `DELETE ${path} failed`);
  }

  return true;
}

const ACTION_PRIORITY = [
  "view",
  "read",
  "manage",
  "create",
  "update",
  "delete",
  "assign",
  "unassign",
  "export",
  "import",
  "convert",
  "send",
  "call",
  "write",
];

const SYSTEM_ROLE_NAMES = ["superadmin", "admin"];
const TOAST_LIFETIME = 3200;

const CRM_VISIBILITY_ENTITIES = [
  { key: "contacts", label: "contacts" },
  { key: "leads", label: "leads" },
  { key: "deals", label: "deals" },
  { key: "tasks", label: "tasks" },
  { key: "activities", label: "activities" },
];

const CRM_VISIBILITY_SCOPES = [
  {
    value: "own",
    label: "permissions_visibility_own",
    description: "permissions_visibility_own_description",
  },
  {
    value: "same_role",
    label: "permissions_visibility_same_role",
    description: "permissions_visibility_same_role_description",
  },
  {
    value: "selected_roles",
    label: "permissions_visibility_selected_roles",
    description: "permissions_visibility_selected_roles_description",
  },
  {
    value: "all",
    label: "permissions_visibility_all",
    description: "permissions_visibility_all_description",
  },
];

function sortActions(actions) {
  return [...actions].sort((a, b) => {
    const ai = ACTION_PRIORITY.indexOf(a);
    const bi = ACTION_PRIORITY.indexOf(b);

    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
}

function normalizeGroups(items) {
  const map = {};

  for (const item of items || []) {
    const resource = String(item.resource || "other").trim();
    const action = String(item.action || "unknown").trim();

    if (!map[resource]) map[resource] = {};
    map[resource][action] = item;
  }

  const resources = Object.keys(map).sort((a, b) => a.localeCompare(b));
  const actionsSet = new Set();

  resources.forEach((resource) => {
    Object.keys(map[resource]).forEach((action) => actionsSet.add(action));
  });

  return {
    resources,
    actions: sortActions([...actionsSet]),
    matrix: map,
  };
}

function prettyResourceName(resource) {
  return String(resource || "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function prettyActionName(action) {
  return String(action || "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function buildCellTitle(permission, checked) {
  if (!permission) return "";
  return [
    permission.code,
    permission.description || `${permission.resource}.${permission.action}`,
    checked ? "Assigned to role" : "Not assigned to role",
  ]
    .filter(Boolean)
    .join(" • ");
}

function normalizeRoleName(roleName) {
  return String(roleName || "").trim().toLowerCase();
}

function isSystemRole(role) {
  return SYSTEM_ROLE_NAMES.includes(normalizeRoleName(role?.role_name));
}

function hasPermission(permissions, code) {
  return Array.isArray(permissions) && permissions.includes(code);
}

function getAssignableOptionRoles(allRoles, managerRoleName) {
  const normalizedManagerRoleName = normalizeRoleName(managerRoleName);

  if (!normalizedManagerRoleName) return [];

  if (normalizedManagerRoleName === "superadmin") {
    return (allRoles || []).filter(
      (role) => normalizeRoleName(role.role_name) !== "superadmin"
    );
  }

  if (normalizedManagerRoleName === "admin") {
    return (allRoles || []).filter(
      (role) => normalizeRoleName(role.role_name) !== "superadmin"
    );
  }

  return (allRoles || []).filter((role) => {
    const roleName = normalizeRoleName(role.role_name);
    return roleName !== "superadmin" && roleName !== "admin";
  });
}

function createDefaultVisibilityRules() {
  return CRM_VISIBILITY_ENTITIES.reduce((acc, entity) => {
    acc[entity.key] = {
      scope: "own",
      allowed_role_ids: [],
    };
    return acc;
  }, {});
}

function normalizeVisibilityRules(apiRules) {
  const next = createDefaultVisibilityRules();

  for (const rule of apiRules || []) {
    if (!rule?.entity_type) continue;

    next[rule.entity_type] = {
      scope: rule.scope || "own",
      allowed_role_ids: Array.isArray(rule.allowed_role_ids_json)
        ? rule.allowed_role_ids_json.map((id) => Number(id)).filter(Boolean)
        : [],
    };
  }

  return next;
}

function getRoleLabel(role) {
  if (!role) return "";
  return `${role.role_name} #${role.ui_id ?? role.id}`;
}

export default function PermissionsPage() {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [assignedPermissionIds, setAssignedPermissionIds] = useState(new Set());
  const [savingKeys, setSavingKeys] = useState({});
  const [bulkSaving, setBulkSaving] = useState(false);
  const [query, setQuery] = useState("");

  const [assignableRoleIds, setAssignableRoleIds] = useState(new Set());
  const [assignableLoading, setAssignableLoading] = useState(false);
  const [assignableSaving, setAssignableSaving] = useState(false);

  const [visibilityRules, setVisibilityRules] = useState(() =>
    createDefaultVisibilityRules()
  );
  const [visibilityLoading, setVisibilityLoading] = useState(false);
  const [visibilitySaving, setVisibilitySaving] = useState(false);

  const [newRoleName, setNewRoleName] = useState("");
  const [creatingRole, setCreatingRole] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const [editRoleName, setEditRoleName] = useState("");
  const [editingRole, setEditingRole] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const [cloningRole, setCloningRole] = useState(false);
  const [deletingRole, setDeletingRole] = useState(false);
  const [toasts, setToasts] = useState([]);

  const [userPermissions, setUserPermissions] = useState(() =>
    getUserPermissions()
  );

  const currentUserRoleId = useMemo(() => {
    const raw = getUserRole();
    return raw == null ? null : String(raw);
  }, [userPermissions]);

  const pushToast = useCallback((type, message) => {
    const id = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

    setToasts((prev) => [...prev, { id, type, message }]);

    window.setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, TOAST_LIFETIME);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  useEffect(() => {
    const syncPermissions = () => setUserPermissions(getUserPermissions());

    window.addEventListener("storage", syncPermissions);
    window.addEventListener("token-changed", syncPermissions);
    window.addEventListener("permissions-updated", syncPermissions);

    return () => {
      window.removeEventListener("storage", syncPermissions);
      window.removeEventListener("token-changed", syncPermissions);
      window.removeEventListener("permissions-updated", syncPermissions);
    };
  }, []);

  const canCreateRole = useMemo(
    () => hasPermission(userPermissions, "roles.create"),
    [userPermissions]
  );

  const canEditRole = useMemo(
    () => hasPermission(userPermissions, "roles.edit"),
    [userPermissions]
  );

  const canDeleteRole = useMemo(
    () => hasPermission(userPermissions, "roles.delete"),
    [userPermissions]
  );

  const canAssignRolePermissions = useMemo(
    () => hasPermission(userPermissions, "role_permissions.assign"),
    [userPermissions]
  );

  const canUnassignRolePermissions = useMemo(
    () => hasPermission(userPermissions, "role_permissions.unassign"),
    [userPermissions]
  );

  const canReadCrmAccess = useMemo(
    () => hasPermission(userPermissions, "crm_access.read"),
    [userPermissions]
  );

  const canUpdateCrmAccess = useMemo(
    () => hasPermission(userPermissions, "crm_access.update"),
    [userPermissions]
  );

  const canManageMatrix = useMemo(
    () => canAssignRolePermissions || canUnassignRolePermissions,
    [canAssignRolePermissions, canUnassignRolePermissions]
  );

  const canCloneRole = useMemo(
    () => canCreateRole && canAssignRolePermissions,
    [canCreateRole, canAssignRolePermissions]
  );

  const uiBusy = useMemo(
    () =>
      bulkSaving ||
      creatingRole ||
      editingRole ||
      cloningRole ||
      deletingRole ||
      assignableSaving ||
      visibilitySaving,
    [
      bulkSaving,
      creatingRole,
      editingRole,
      cloningRole,
      deletingRole,
      assignableSaving,
      visibilitySaving,
    ]
  );

  const matrixBusy = useMemo(
    () => uiBusy || assignableLoading || visibilityLoading,
    [uiBusy, assignableLoading, visibilityLoading]
  );

  const selectedRole = useMemo(() => {
    return roles.find((r) => String(r.id) === String(selectedRoleId)) || null;
  }, [roles, selectedRoleId]);

  const selectedRoleName = useMemo(
    () => normalizeRoleName(selectedRole?.role_name),
    [selectedRole]
  );

  const selectedRoleIsSystem = useMemo(() => {
    return ["superadmin", "admin"].includes(selectedRoleName);
  }, [selectedRoleName]);

  const selectedRoleIsSuperAdmin = useMemo(() => {
    return selectedRoleName === "superadmin";
  }, [selectedRoleName]);

  const currentUserRoleName = useMemo(() => {
    const me = roles.find((r) => String(r.id) === String(currentUserRoleId));
    return normalizeRoleName(me?.role_name);
  }, [roles, currentUserRoleId]);

  const canRenameSelectedRole = useMemo(() => {
    return canEditRole && !!selectedRole && !selectedRoleIsSystem;
  }, [canEditRole, selectedRole, selectedRoleIsSystem]);

  const canDeleteSelectedRole = useMemo(() => {
    return canDeleteRole && !!selectedRole && !selectedRoleIsSystem;
  }, [canDeleteRole, selectedRole, selectedRoleIsSystem]);

  const canManageAssignableRoles = useMemo(() => {
    if (!canEditRole || !selectedRole) return false;

    if (currentUserRoleName === "superadmin") {
      return selectedRoleName !== "superadmin";
    }

    if (currentUserRoleName === "admin") {
      return selectedRoleName !== "superadmin";
    }

    return selectedRoleName !== "superadmin" && selectedRoleName !== "admin";
  }, [canEditRole, selectedRole, currentUserRoleName, selectedRoleName]);

  const canManageVisibilityRules = useMemo(() => {
    if (!canUpdateCrmAccess || !selectedRole) return false;
    return selectedRoleName !== "superadmin";
  }, [canUpdateCrmAccess, selectedRole, selectedRoleName]);

  const assignableRoleOptions = useMemo(() => {
    return getAssignableOptionRoles(roles, selectedRoleName);
  }, [roles, selectedRoleName]);

  const visibilityRoleOptions = useMemo(() => {
    return (roles || []).filter((role) => {
      const name = normalizeRoleName(role.role_name);
      return name !== "superadmin";
    });
  }, [roles]);

  const refreshCurrentSessionIfOwnRoleChanged = useCallback(
    async (changedRoleId) => {
      if (!changedRoleId || !currentUserRoleId) return;
      if (String(changedRoleId) !== String(currentUserRoleId)) return;

      await refreshSessionAndPermissions(appConfig.API_BASE_URL);
      setUserPermissions(getUserPermissions());
      window.dispatchEvent(new Event("token-changed"));
    },
    [currentUserRoleId]
  );

  const loadAssignedPermissions = useCallback(async (roleId) => {
    if (!roleId) {
      setAssignedPermissionIds(new Set());
      return;
    }

    const data = await apiGet(`/auth/permissions/roles/${roleId}`);
    const ids = new Set((data || []).map((x) => x.permission_id));
    setAssignedPermissionIds(ids);
  }, []);

  const loadAssignableRoles = useCallback(async (roleId) => {
    if (!roleId) {
      setAssignableRoleIds(new Set());
      return;
    }

    setAssignableLoading(true);
    try {
      const data = await apiGet(`/auth/roles/${roleId}/assignable`);
      const normalized = Array.isArray(data) ? data : [];
      const ids = new Set(normalized.map((x) => String(x.id)));
      setAssignableRoleIds(ids);
    } finally {
      setAssignableLoading(false);
    }
  }, []);

  const loadVisibilityRules = useCallback(
    async (roleId) => {
      if (!roleId || !canReadCrmAccess) {
        setVisibilityRules(createDefaultVisibilityRules());
        return;
      }

      setVisibilityLoading(true);
      try {
        const data = await apiGet(
          `/api/system/crm-access/roles/${roleId}/rules`
        );
        setVisibilityRules(normalizeVisibilityRules(data?.rules || []));
      } finally {
        setVisibilityLoading(false);
      }
    },
    [canReadCrmAccess]
  );

  const reloadRoles = useCallback(async () => {
    const rolesData = await apiGet("/auth/roles/all?skip=0&limit=200");
    const nextRoles = Array.isArray(rolesData) ? rolesData : [];
    setRoles(nextRoles);
    return nextRoles;
  }, []);

  const openCreateModal = () => {
    if (!canCreateRole) {
      pushToast("error", "Permission denied: roles.create");
      return;
    }

    setNewRoleName("");
    setIsCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    if (creatingRole) return;
    setIsCreateModalOpen(false);
    setNewRoleName("");
  };

  const openEditModal = () => {
    if (!canEditRole) {
      pushToast("error", "Permission denied: roles.edit");
      return;
    }

    if (!selectedRoleId || !selectedRole) {
      pushToast("error", "Role is not selected");
      return;
    }

    if (selectedRoleIsSystem) {
      pushToast("error", "Cannot edit system role");
      return;
    }

    setEditRoleName(selectedRole.role_name || "");
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    if (editingRole) return;
    setIsEditModalOpen(false);
    setEditRoleName("");
  };

  const handleCreateRole = async () => {
    if (!canCreateRole) {
      pushToast("error", "Permission denied: roles.create");
      return;
    }

    if (!newRoleName.trim()) {
      pushToast("error", "Role name is required");
      return;
    }

    try {
      setCreatingRole(true);

      const created = await apiPost("/auth/role/", {
        role_name: newRoleName.trim(),
      });

      const nextRoles = await reloadRoles();
      const newId = String(created.id);
      const existsInList = nextRoles.some((r) => String(r.id) === newId);

      if (existsInList) {
        setSelectedRoleId(newId);
        setAssignedPermissionIds(new Set());
        setAssignableRoleIds(new Set());
        setVisibilityRules(createDefaultVisibilityRules());

        await Promise.all([
          loadAssignedPermissions(newId),
          loadAssignableRoles(newId),
          loadVisibilityRules(newId),
        ]);
      }

      setNewRoleName("");
      setIsCreateModalOpen(false);
      pushToast("success", "Role created successfully");
    } catch (e) {
      pushToast("error", e.message || t("error_generic"));
    } finally {
      setCreatingRole(false);
    }
  };

  const handleEditRole = async () => {
    if (!canEditRole) {
      pushToast("error", "Permission denied: roles.edit");
      return;
    }

    if (!selectedRoleId || !selectedRole) {
      pushToast("error", "Role is not selected");
      return;
    }

    if (selectedRoleIsSystem) {
      pushToast("error", "Cannot edit system role");
      return;
    }

    if (!editRoleName.trim()) {
      pushToast("error", "Role name is required");
      return;
    }

    if (editRoleName.trim() === String(selectedRole.role_name || "").trim()) {
      closeEditModal();
      return;
    }

    try {
      setEditingRole(true);

      const updated = await apiPut(`/auth/role/${selectedRoleId}`, {
        role_name: editRoleName.trim(),
      });

      const nextRoles = await reloadRoles();
      const updatedId = String(updated.id);
      const existsInList = nextRoles.some((r) => String(r.id) === updatedId);

      if (existsInList) {
        setSelectedRoleId(updatedId);
      }

      setEditRoleName("");
      setIsEditModalOpen(false);
      pushToast("success", "Role updated successfully");
    } catch (e) {
      pushToast("error", e.message || t("error_generic"));
    } finally {
      setEditingRole(false);
    }
  };

  const handleCloneRole = async () => {
    if (!canCloneRole) {
      pushToast(
        "error",
        "Permission denied: roles.create and role_permissions.assign required"
      );
      return;
    }

    if (!selectedRoleId) return;

    try {
      setCloningRole(true);

      const currentRole = roles.find(
        (r) => String(r.id) === String(selectedRoleId)
      );

      if (!currentRole) {
        throw new Error("Role not found");
      }

      const newRole = await apiPost("/auth/role/", {
        role_name: `Copy of ${currentRole.role_name}`,
      });

      const newRoleId = String(newRole.id);
      const currentPermissions = Array.from(assignedPermissionIds);
      const currentAssignableIds = Array.from(assignableRoleIds).map((id) =>
        Number(id)
      );

      await Promise.all(
        currentPermissions.map((permissionId) =>
          apiPost(`/auth/permissions/roles/${newRoleId}`, {
            permission_id: permissionId,
          })
        )
      );

      await apiPut(`/auth/roles/${newRoleId}/assignable`, {
        target_role_ids: currentAssignableIds,
      });

      if (canUpdateCrmAccess && !selectedRoleIsSuperAdmin) {
        await apiPut(`/api/system/crm-access/roles/${newRoleId}/rules`, {
          rules: visibilityRules,
        });
      }

      await reloadRoles();
      setSelectedRoleId(newRoleId);

      await Promise.all([
        loadAssignedPermissions(newRoleId),
        loadAssignableRoles(newRoleId),
        loadVisibilityRules(newRoleId),
      ]);

      pushToast("success", "Role cloned successfully");
    } catch (e) {
      pushToast("error", e.message || t("error_generic"));
    } finally {
      setCloningRole(false);
    }
  };

  const handleDeleteRole = async () => {
    if (!canDeleteRole) {
      pushToast("error", "Permission denied: roles.delete");
      return;
    }

    if (!selectedRoleId) return;

    const role = roles.find((r) => String(r.id) === String(selectedRoleId));
    if (!role) return;

    if (isSystemRole(role)) {
      pushToast("error", "This default role cannot be deleted");
      return;
    }

    const confirmed = window.confirm(
      t("permissions_delete_role_confirm", { name: role.role_name }),
    );
    if (!confirmed) return;

    try {
      setDeletingRole(true);

      await apiDelete(`/auth/role/${selectedRoleId}`);

      const nextRoles = await reloadRoles();

      if (nextRoles.length > 0) {
        const fallbackRole = nextRoles[0];
        const newId = String(fallbackRole.id);
        setSelectedRoleId(newId);

        await Promise.all([
          loadAssignedPermissions(newId),
          loadAssignableRoles(newId),
          loadVisibilityRules(newId),
        ]);
      } else {
        setSelectedRoleId("");
        setAssignedPermissionIds(new Set());
        setAssignableRoleIds(new Set());
        setVisibilityRules(createDefaultVisibilityRules());
      }

      pushToast("success", "Role deleted successfully");
    } catch (e) {
      console.error("DELETE ROLE ERROR:", e);
      pushToast("error", e.message || t("error_generic"));
    } finally {
      setDeletingRole(false);
    }
  };

  const handleToggleAssignableRole = (targetRoleId) => {
    if (!selectedRoleId || assignableSaving || !canManageAssignableRoles) return;

    const normalizedTargetRoleId = String(targetRoleId);

    setAssignableRoleIds((prev) => {
      const next = new Set(prev);

      if (next.has(normalizedTargetRoleId)) {
        next.delete(normalizedTargetRoleId);
      } else {
        next.add(normalizedTargetRoleId);
      }

      return next;
    });
  };

  const handleSaveAssignableRoles = async () => {
    if (!selectedRoleId) return;

    if (!canManageAssignableRoles) {
      pushToast(
        "error",
        selectedRoleIsSystem
          ? "Cannot edit system role"
          : "Permission denied: roles.edit"
      );
      return;
    }

    try {
      setAssignableSaving(true);

      await apiPut(`/auth/roles/${selectedRoleId}/assignable`, {
        target_role_ids: Array.from(assignableRoleIds).map((id) => Number(id)),
      });

      await loadAssignableRoles(selectedRoleId);
      await refreshCurrentSessionIfOwnRoleChanged(selectedRoleId);

      pushToast("success", "Assignable roles updated successfully");
    } catch (e) {
      await loadAssignableRoles(selectedRoleId);
      pushToast("error", e.message || t("error_generic"));
    } finally {
      setAssignableSaving(false);
    }
  };

  const handleVisibilityScopeChange = (entityType, scope) => {
    if (!canManageVisibilityRules || visibilitySaving) return;

    setVisibilityRules((prev) => ({
      ...prev,
      [entityType]: {
        ...(prev[entityType] || { allowed_role_ids: [] }),
        scope,
        allowed_role_ids:
          scope === "selected_roles"
            ? prev[entityType]?.allowed_role_ids || []
            : [],
      },
    }));
  };

  const handleToggleVisibilityRole = (entityType, roleId) => {
    if (!canManageVisibilityRules || visibilitySaving) return;

    const numericRoleId = Number(roleId);

    setVisibilityRules((prev) => {
      const current = prev[entityType] || {
        scope: "selected_roles",
        allowed_role_ids: [],
      };

      const currentIds = new Set(
        (current.allowed_role_ids || []).map((id) => Number(id))
      );

      if (currentIds.has(numericRoleId)) {
        currentIds.delete(numericRoleId);
      } else {
        currentIds.add(numericRoleId);
      }

      return {
        ...prev,
        [entityType]: {
          ...current,
          scope: "selected_roles",
          allowed_role_ids: Array.from(currentIds),
        },
      };
    });
  };

  const handleSaveVisibilityRules = async () => {
    if (!selectedRoleId) return;

    if (!canManageVisibilityRules) {
      pushToast(
        "error",
        selectedRoleIsSuperAdmin
          ? "SuperAdmin visibility cannot be restricted"
          : "Permission denied: crm_access.update"
      );
      return;
    }

    try {
      setVisibilitySaving(true);

      await apiPut(`/api/system/crm-access/roles/${selectedRoleId}/rules`, {
        rules: visibilityRules,
      });

      await loadVisibilityRules(selectedRoleId);
      await refreshCurrentSessionIfOwnRoleChanged(selectedRoleId);

      pushToast("success", "CRM visibility rules updated successfully");
    } catch (e) {
      await loadVisibilityRules(selectedRoleId);
      pushToast("error", e.message || t("error_generic"));
    } finally {
      setVisibilitySaving(false);
    }
  };

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        setLoading(true);

        const [rolesData, permissionsData] = await Promise.all([
          apiGet("/auth/roles/all?skip=0&limit=200"),
          apiGet("/auth/permissions?skip=0&limit=1000"),
        ]);

        if (!mounted) return;

        const nextRoles = Array.isArray(rolesData) ? rolesData : [];
        const nextPermissions = Array.isArray(permissionsData)
          ? permissionsData
          : [];

        setRoles(nextRoles);
        setPermissions(nextPermissions);

        if (nextRoles.length > 0) {
          const firstRoleId = String(nextRoles[0].id);
          setSelectedRoleId(firstRoleId);

          await Promise.all([
            loadAssignedPermissions(firstRoleId),
            loadAssignableRoles(firstRoleId),
            loadVisibilityRules(firstRoleId),
          ]);
        } else {
          setSelectedRoleId("");
          setAssignedPermissionIds(new Set());
          setAssignableRoleIds(new Set());
          setVisibilityRules(createDefaultVisibilityRules());
        }
      } catch (e) {
        if (!mounted) return;
        pushToast("error", e.message || t("error_generic"));
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [
    loadAssignedPermissions,
    loadAssignableRoles,
    loadVisibilityRules,
    t,
    pushToast,
  ]);

  useEffect(() => {
    let mounted = true;

    (async () => {
      if (!selectedRoleId) return;

      try {
        await Promise.all([
          loadAssignedPermissions(selectedRoleId),
          loadAssignableRoles(selectedRoleId),
          loadVisibilityRules(selectedRoleId),
        ]);
      } catch (e) {
        if (!mounted) return;
        pushToast("error", e.message || t("error_generic"));
      }
    })();

    return () => {
      mounted = false;
    };
  }, [
    selectedRoleId,
    loadAssignedPermissions,
    loadAssignableRoles,
    loadVisibilityRules,
    t,
    pushToast,
  ]);

  useEffect(() => {
    if (!isCreateModalOpen) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape" && !creatingRole) closeCreateModal();
      if (e.key === "Enter" && canCreateRole && !creatingRole) {
        handleCreateRole();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isCreateModalOpen, creatingRole, canCreateRole, newRoleName]);

  useEffect(() => {
    if (!isEditModalOpen) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape" && !editingRole) closeEditModal();
      if (e.key === "Enter" && canRenameSelectedRole && !editingRole) {
        handleEditRole();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    isEditModalOpen,
    editingRole,
    canRenameSelectedRole,
    editRoleName,
    selectedRoleId,
  ]);

  const filteredPermissions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return permissions;

    return (permissions || []).filter((item) => {
      const resource = String(item.resource || "").toLowerCase();
      const action = String(item.action || "").toLowerCase();
      const code = String(item.code || "").toLowerCase();
      const description = String(item.description || "").toLowerCase();

      return (
        resource.includes(q) ||
        action.includes(q) ||
        code.includes(q) ||
        description.includes(q)
      );
    });
  }, [permissions, query]);

  const { resources, actions, matrix } = useMemo(
    () => normalizeGroups(filteredPermissions),
    [filteredPermissions]
  );

  const markSaving = (permissionId, value) => {
    if (!selectedRoleId) return;
    const key = `${selectedRoleId}_${permissionId}`;
    setSavingKeys((prev) => ({ ...prev, [key]: value }));
  };

  const optimisticAssign = (permissionId) => {
    setAssignedPermissionIds((prev) => {
      const next = new Set(prev);
      next.add(permissionId);
      return next;
    });
  };

  const optimisticUnassign = (permissionId) => {
    setAssignedPermissionIds((prev) => {
      const next = new Set(prev);
      next.delete(permissionId);
      return next;
    });
  };

  const togglePermission = async (permission) => {
    if (!selectedRoleId || !permission?.id || bulkSaving) return;

    const roleId = String(selectedRoleId);
    const permissionId = permission.id;
    const isChecked = assignedPermissionIds.has(permissionId);

    if (isChecked && !canUnassignRolePermissions) {
      pushToast("error", "Permission denied: role_permissions.unassign");
      return;
    }

    if (!isChecked && !canAssignRolePermissions) {
      pushToast("error", "Permission denied: role_permissions.assign");
      return;
    }

    try {
      markSaving(permissionId, true);

      if (isChecked) {
        optimisticUnassign(permissionId);
        await apiDelete(`/auth/permissions/roles/${roleId}/${permissionId}`);
        await refreshCurrentSessionIfOwnRoleChanged(roleId);
        pushToast("success", "Permission removed from role");
      } else {
        optimisticAssign(permissionId);
        await apiPost(`/auth/permissions/roles/${roleId}`, {
          permission_id: permissionId,
        });
        await refreshCurrentSessionIfOwnRoleChanged(roleId);
        pushToast("success", "Permission assigned to role");
      }

      await loadAssignedPermissions(roleId);
    } catch (e) {
      await loadAssignedPermissions(roleId);
      pushToast("error", e.message || t("error_generic"));
    } finally {
      markSaving(permissionId, false);
    }
  };

  const bulkTogglePermissions = async (items, shouldAssign, successText) => {
    if (!selectedRoleId || items.length === 0) return;

    if (shouldAssign && !canAssignRolePermissions) {
      pushToast("error", "Permission denied: role_permissions.assign");
      return;
    }

    if (!shouldAssign && !canUnassignRolePermissions) {
      pushToast("error", "Permission denied: role_permissions.unassign");
      return;
    }

    const roleId = String(selectedRoleId);
    const ids = items.map((x) => x.id);

    try {
      setBulkSaving(true);

      setSavingKeys((prev) => {
        const next = { ...prev };
        ids.forEach((id) => {
          next[`${roleId}_${id}`] = true;
        });
        return next;
      });

      setAssignedPermissionIds((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => {
          if (shouldAssign) next.add(id);
          else next.delete(id);
        });
        return next;
      });

      await Promise.all(
        items.map((permission) =>
          shouldAssign
            ? apiPost(`/auth/permissions/roles/${roleId}`, {
                permission_id: permission.id,
              })
            : apiDelete(`/auth/permissions/roles/${roleId}/${permission.id}`)
        )
      );

      await refreshCurrentSessionIfOwnRoleChanged(roleId);
      await loadAssignedPermissions(roleId);
      pushToast("success", successText);
    } catch (e) {
      await loadAssignedPermissions(roleId);
      pushToast("error", e.message || t("error_generic"));
    } finally {
      setSavingKeys((prev) => {
        const next = { ...prev };
        ids.forEach((id) => {
          next[`${roleId}_${id}`] = false;
        });
        return next;
      });

      setBulkSaving(false);
    }
  };

  const toggleResourceRow = async (resource, checked) => {
    const rowItems = actions
      .map((action) => matrix?.[resource]?.[action] || null)
      .filter(Boolean);

    const targetItems = rowItems.filter((permission) => {
      const has = assignedPermissionIds.has(permission.id);
      return checked ? !has : has;
    });

    await bulkTogglePermissions(
      targetItems,
      checked,
      checked
        ? "All permissions assigned for resource"
        : "All permissions removed for resource"
    );
  };

  const toggleActionColumn = async (action, checked) => {
    const colItems = resources
      .map((resource) => matrix?.[resource]?.[action] || null)
      .filter(Boolean);

    const targetItems = colItems.filter((permission) => {
      const has = assignedPermissionIds.has(permission.id);
      return checked ? !has : has;
    });

    await bulkTogglePermissions(
      targetItems,
      checked,
      checked ? "Column permissions assigned" : "Column permissions removed"
    );
  };

  const isRowFullyChecked = (resource) => {
    const rowItems = actions
      .map((action) => matrix?.[resource]?.[action] || null)
      .filter(Boolean);

    if (rowItems.length === 0) return false;
    return rowItems.every((item) => assignedPermissionIds.has(item.id));
  };

  const isColumnFullyChecked = (action) => {
    const colItems = resources
      .map((resource) => matrix?.[resource]?.[action] || null)
      .filter(Boolean);

    if (colItems.length === 0) return false;
    return colItems.every((item) => assignedPermissionIds.has(item.id));
  };

    if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.stateCard}>{t("loading")}...</div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.toastStack}>
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`${styles.toast} ${
              toast.type === "error" ? styles.toastError : styles.toastSuccess
            }`}
          >
            <div className={styles.toastContent}>{toast.message}</div>
            <button
              type="button"
              className={styles.toastClose}
              onClick={() => removeToast(toast.id)}
              aria-label={t("close")}
            >
              <i className="lni lni-xmark"></i>
            </button>
          </div>
        ))}
      </div>

      <div className={styles.topBar}>
        <div>
          <h1 className={styles.title}>
            {t("permissions_page_title", {
              defaultValue: "Role Permissions",
            })}
          </h1>
          <div className={styles.subtitle}>
            {t("permissions_page_hint", {
              defaultValue:
                "Manage role permissions, assignable roles and CRM data visibility.",
            })}
          </div>
        </div>

        <div className={styles.topActions}>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={() => window.location.reload()}
            disabled={uiBusy}
          >
            {t("reset")}
          </button>

          <button
            type="button"
            className={styles.secondaryButton}
            onClick={openCreateModal}
            disabled={uiBusy || !canCreateRole}
            title={
              !canCreateRole
                ? "Permission denied: roles.create"
                : "Create role"
            }
          >
            {t("create_role", { defaultValue: "Create role" })}
          </button>

          <button
            type="button"
            className={styles.secondaryButton}
            onClick={openEditModal}
            disabled={!selectedRoleId || uiBusy || !canRenameSelectedRole}
            title={
              !canRenameSelectedRole
                ? selectedRoleIsSystem
                  ? "Cannot edit system role"
                  : "Permission denied: roles.edit"
                : "Edit role"
            }
          >
            {editingRole ? "Saving..." : t("edit_role", { defaultValue: "Edit role" })}
          </button>

          <button
            type="button"
            className={styles.secondaryButton}
            onClick={handleCloneRole}
            disabled={!selectedRoleId || uiBusy || !canCloneRole}
            title={
              !canCloneRole
                ? "roles.create and role_permissions.assign required"
                : "Clone role"
            }
          >
            {cloningRole ? "Cloning..." : t("clone_role", { defaultValue: "Clone role" })}
          </button>

          <button
            type="button"
            className={`${styles.secondaryButton} ${
              !canDeleteSelectedRole
                ? styles.disabledDangerButton
                : styles.dangerButton
            }`}
            onClick={handleDeleteRole}
            disabled={!selectedRoleId || uiBusy || !canDeleteSelectedRole}
            title={
              !canDeleteSelectedRole
                ? selectedRoleIsSystem
                  ? "This default role cannot be deleted"
                  : "Permission denied: roles.delete"
                : "Delete role"
            }
          >
            {deletingRole
              ? "Deleting..."
              : t("delete_role", { defaultValue: "Delete role" })}
          </button>
        </div>
      </div>

      <div className={styles.toolbar}>
        <div className={styles.toolbarBlock}>
          <label className={styles.label}>
            {t("permissions_select_role", { defaultValue: "Role" })}
          </label>
          <select
            className={styles.select}
            value={selectedRoleId}
            onChange={(e) => setSelectedRoleId(e.target.value)}
            disabled={uiBusy}
          >
            {roles.map((role) => (
              <option key={role.id} value={role.id}>
                {getRoleLabel(role)}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.toolbarBlock}>
          <label className={styles.label}>
            {t("search", { defaultValue: "Search" })}
          </label>
          <input
            className={styles.input}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("permissions_search_placeholder", {
              defaultValue: "resource, action, code...",
            })}
            disabled={uiBusy}
          />
        </div>

        <div className={styles.roleBadgeWrap}>
          <div className={styles.roleBadge}>
            {uiBusy ? "Saving..." : selectedRole?.role_name || "-"}
          </div>
        </div>
      </div>

      <div className={styles.stateCard} style={{ marginBottom: 12 }}>
        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "flex-start",
            justifyContent: "space-between",
            flexWrap: "wrap",
          }}
        >
          <div>
            <div style={{ fontWeight: 900, marginBottom: 4 }}>
              {t("assignable_roles_title", {
                defaultValue: "Assignable roles",
              })}
            </div>
            <div style={{ fontSize: 12, opacity: 0.78 }}>
              {canManageAssignableRoles
                ? t("assignable_roles_hint", {
                    defaultValue:
                      "Choose which roles the selected role can assign to employees.",
                  })
                : t("assignable_roles_system_role_hint", {
                    defaultValue:
                      "You cannot edit assignable roles for the selected role.",
                  })}
            </div>
          </div>

          <button
            type="button"
            className={styles.primaryButton}
            onClick={handleSaveAssignableRoles}
            disabled={!selectedRoleId || matrixBusy || !canManageAssignableRoles}
            title={
              !canManageAssignableRoles
                ? selectedRoleIsSystem
                  ? "Cannot edit system role"
                  : "Permission denied: roles.edit"
                : "Save"
            }
          >
            {assignableSaving ? "Saving..." : t("save", { defaultValue: "Save" })}
          </button>
        </div>

        {!selectedRoleId ? (
          <div style={{ marginTop: 12, opacity: 0.7 }}>{t("permissions_role_not_selected")}</div>
        ) : assignableLoading ? (
          <div style={{ marginTop: 12, opacity: 0.7 }}>{t("loading")}...</div>
        ) : (
          <div
            style={{
              marginTop: 14,
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 10,
            }}
          >
            {assignableRoleOptions.map((role) => {
              const roleId = String(role.id);
              const checked = assignableRoleIds.has(roleId);
              const selfRole = roleId === String(selectedRoleId);

              return (
                <label
                  key={role.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: 12,
                    padding: "10px 12px",
                    background: "rgba(255,255,255,0.03)",
                    opacity: assignableSaving || !canManageAssignableRoles ? 0.75 : 1,
                  }}
                  title={
                    selfRole
                      ? "Allow this role to assign itself"
                      : role.role_name
                  }
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={matrixBusy || !canManageAssignableRoles}
                    onChange={() => handleToggleAssignableRole(role.id)}
                  />

                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 800 }}>{role.role_name}</div>
                    <div style={{ fontSize: 11, opacity: 0.62 }}>
                      #{role.ui_id ?? role.id}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </div>

      <div className={styles.stateCard} style={{ marginBottom: 12 }}>
        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "flex-start",
            justifyContent: "space-between",
            flexWrap: "wrap",
          }}
        >
          <div>
            <div style={{ fontWeight: 900, marginBottom: 4 }}>
              {t("permissions_crm_visibility")}
            </div>
            <div style={{ fontSize: 12, opacity: 0.78 }}>
              {t("permissions_crm_visibility_description")}
            </div>
          </div>

          <button
            type="button"
            className={styles.primaryButton}
            onClick={handleSaveVisibilityRules}
            disabled={!selectedRoleId || matrixBusy || !canManageVisibilityRules}
            title={
              !canManageVisibilityRules
                ? selectedRoleIsSuperAdmin
                  ? "SuperAdmin visibility cannot be restricted"
                  : "Permission denied: crm_access.update"
                : "Save CRM visibility"
            }
          >
            {visibilitySaving ? "Saving..." : "Save visibility"}
          </button>
        </div>

        {!canReadCrmAccess ? (
          <div style={{ marginTop: 12, opacity: 0.7 }}>
            {t("permissions_denied_crm_read")}
          </div>
        ) : !selectedRoleId ? (
          <div style={{ marginTop: 12, opacity: 0.7 }}>{t("permissions_role_not_selected")}</div>
        ) : visibilityLoading ? (
          <div style={{ marginTop: 12, opacity: 0.7 }}>{t("loading")}...</div>
        ) : selectedRoleIsSuperAdmin ? (
          <div
            style={{
              marginTop: 14,
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 14,
              padding: "14px 16px",
              background: "rgba(255,255,255,0.03)",
              fontSize: 13,
              opacity: 0.82,
            }}
          >
            {t("permissions_superadmin_visibility")}
          </div>
        ) : (
          <div
            style={{
              marginTop: 14,
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: 12,
            }}
          >
            {CRM_VISIBILITY_ENTITIES.map((entity) => {
              const rule = visibilityRules[entity.key] || {
                scope: "own",
                allowed_role_ids: [],
              };

              const selectedIds = new Set(
                (rule.allowed_role_ids || []).map((id) => Number(id))
              );

              return (
                <div
                  key={entity.key}
                  style={{
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: 16,
                    padding: 14,
                    background: "rgba(255,255,255,0.03)",
                    minWidth: 0,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 10,
                      marginBottom: 10,
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 900 }}>{t(entity.label)}</div>
                      <div style={{ fontSize: 11, opacity: 0.62 }}>
                        {entity.key}
                      </div>
                    </div>

                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 800,
                        padding: "4px 8px",
                        borderRadius: 999,
                        background: "rgba(255,255,255,0.06)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {rule.scope}
                    </div>
                  </div>

                  <div style={{ display: "grid", gap: 8 }}>
                    {CRM_VISIBILITY_SCOPES.map((scope) => (
                      <label
                        key={scope.value}
                        style={{
                          display: "flex",
                          gap: 10,
                          alignItems: "flex-start",
                          padding: "8px 10px",
                          borderRadius: 12,
                          background:
                            rule.scope === scope.value
                              ? "rgba(255,255,255,0.08)"
                              : "rgba(255,255,255,0.025)",
                          border:
                            rule.scope === scope.value
                              ? "1px solid rgba(255,255,255,0.16)"
                              : "1px solid rgba(255,255,255,0.05)",
                          cursor: canManageVisibilityRules
                            ? "pointer"
                            : "not-allowed",
                          opacity:
                            visibilitySaving || !canManageVisibilityRules
                              ? 0.72
                              : 1,
                        }}
                      >
                        <input
                          type="radio"
                          name={`crm_visibility_${entity.key}`}
                          checked={rule.scope === scope.value}
                          disabled={
                            visibilitySaving || !canManageVisibilityRules
                          }
                          onChange={() =>
                            handleVisibilityScopeChange(
                              entity.key,
                              scope.value
                            )
                          }
                        />

                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 800, fontSize: 13 }}>
                            {t(scope.label)}
                          </div>
                          <div style={{ fontSize: 11, opacity: 0.7 }}>
                            {t(scope.description)}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>

                  {rule.scope === "selected_roles" ? (
                    <div style={{ marginTop: 12 }}>
                      <div
                        style={{
                          fontWeight: 800,
                          fontSize: 12,
                          marginBottom: 8,
                          opacity: 0.82,
                        }}
                      >
                        {t("permissions_included_owner_roles")}
                      </div>

                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fit, minmax(130px, 1fr))",
                          gap: 8,
                        }}
                      >
                        {visibilityRoleOptions.map((role) => {
                          const roleId = Number(role.id);
                          const checked = selectedIds.has(roleId);
                          const isCurrentEditedRole =
                            String(role.id) === String(selectedRoleId);

                          return (
                            <label
                              key={role.id}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                padding: "8px 9px",
                                borderRadius: 11,
                                background: checked
                                  ? "rgba(255,255,255,0.08)"
                                  : "rgba(255,255,255,0.025)",
                                border: checked
                                  ? "1px solid rgba(255,255,255,0.15)"
                                  : "1px solid rgba(255,255,255,0.05)",
                                cursor: canManageVisibilityRules
                                  ? "pointer"
                                  : "not-allowed",
                              }}
                              title={
                                isCurrentEditedRole
                                  ? "Current user's own records are always included by backend"
                                  : getRoleLabel(role)
                              }
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={
                                  visibilitySaving ||
                                  !canManageVisibilityRules
                                }
                                onChange={() =>
                                  handleToggleVisibilityRole(
                                    entity.key,
                                    role.id
                                  )
                                }
                              />

                              <div style={{ minWidth: 0 }}>
                                <div
                                  style={{
                                    fontWeight: 800,
                                    fontSize: 12,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {role.role_name}
                                </div>
                                <div style={{ fontSize: 10, opacity: 0.62 }}>
                                  #{role.ui_id ?? role.id}
                                </div>
                              </div>
                            </label>
                          );
                        })}
                      </div>

                      <div
                        style={{
                          marginTop: 8,
                          fontSize: 11,
                          opacity: 0.62,
                        }}
                      >
                        {t("permissions_own_records_always_included")}
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {resources.length === 0 || actions.length === 0 ? (
        <div className={styles.stateCard}>
          {t("permissions_empty", {
            defaultValue: "No permissions found",
          })}
        </div>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.matrixTable}>
            <thead>
              <tr>
                <th className={styles.resourceCol}>
                  {t("resource", { defaultValue: "Resource" })}
                </th>

                <th
                  className={styles.bulkCol}
                  title={t("permissions_toggle_row")}
                >
                  {t("all")}
                </th>

                {actions.map((action) => (
                  <th
                    key={action}
                    className={styles.actionCol}
                    title={`${prettyActionName(action)} • toggle whole column`}
                  >
                    <div className={styles.headerCell}>
                      <span className={styles.headerText}>
                        {prettyActionName(action)}
                      </span>
                      <input
                        type="checkbox"
                        checked={isColumnFullyChecked(action)}
                        onChange={(e) =>
                          toggleActionColumn(action, e.target.checked)
                        }
                        title={`${prettyActionName(action)} • toggle whole column`}
                        disabled={matrixBusy || !canManageMatrix}
                      />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {resources.map((resource) => (
                <tr key={resource}>
                  <td
                    className={styles.resourceCell}
                    title={`Resource: ${resource}`}
                  >
                    <div className={styles.resourceName}>
                      {prettyResourceName(resource)}
                    </div>
                    <div className={styles.resourceKey}>{resource}</div>
                  </td>

                  <td className={styles.bulkCell}>
                    <input
                      type="checkbox"
                      checked={isRowFullyChecked(resource)}
                      onChange={(e) =>
                        toggleResourceRow(resource, e.target.checked)
                      }
                      title={`Toggle all permissions for ${resource}`}
                      disabled={matrixBusy || !canManageMatrix}
                    />
                  </td>

                  {actions.map((action) => {
                    const permission = matrix?.[resource]?.[action] || null;

                    if (!permission) {
                      return (
                        <td
                          key={`${resource}_${action}`}
                          className={styles.emptyCell}
                          title={`No permission for ${resource}.${action}`}
                        >
                          —
                        </td>
                      );
                    }

                    const checked = assignedPermissionIds.has(permission.id);
                    const savingKey = `${selectedRoleId}_${permission.id}`;
                    const saving = !!savingKeys[savingKey];

                    return (
                      <td
                        key={`${resource}_${action}`}
                        className={styles.checkCell}
                      >
                        <label
                          className={`${styles.checkboxWrap} ${
                            saving ? styles.checkboxWrapSaving : ""
                          }`}
                          title={buildCellTitle(permission, checked)}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={
                              saving ||
                              bulkSaving ||
                              !selectedRoleId ||
                              uiBusy ||
                              (checked
                                ? !canUnassignRolePermissions
                                : !canAssignRolePermissions)
                            }
                            onChange={() => togglePermission(permission)}
                            title={buildCellTitle(permission, checked)}
                          />
                        </label>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isCreateModalOpen ? (
        <div
          className={styles.modalOverlay}
          onClick={closeCreateModal}
          role="presentation"
        >
          <div
            className={styles.modalCard}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-role-modal-title"
          >
            <div className={styles.modalHeader}>
              <div>
                <h2 id="create-role-modal-title" className={styles.modalTitle}>
                  {t("create_role", { defaultValue: "Create role" })}
                </h2>
                <div className={styles.modalSubtitle}>
                  {t("permissions_create_role_hint")}
                </div>
              </div>

              <button
                type="button"
                className={styles.modalCloseButton}
                onClick={closeCreateModal}
                disabled={creatingRole}
                aria-label={t("close")}
              >
                <i className="lni lni-xmark"></i>
              </button>
            </div>

            <div className={styles.modalBody}>
              <div className={styles.modalField}>
                <label className={styles.label}>{t("permissions_role_name")}</label>
                <input
                  autoFocus
                  className={styles.input}
                  value={newRoleName}
                  onChange={(e) => setNewRoleName(e.target.value)}
                  placeholder={t("permissions_role_name_placeholder")}
                  disabled={creatingRole || !canCreateRole}
                />
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={closeCreateModal}
                disabled={creatingRole}
              >
                {t("cancel", { defaultValue: "Cancel" })}
              </button>

              <button
                type="button"
                className={styles.primaryButton}
                onClick={handleCreateRole}
                disabled={creatingRole || !newRoleName.trim() || !canCreateRole}
                title={
                  !canCreateRole
                    ? "Permission denied: roles.create"
                    : "Create"
                }
              >
                {creatingRole ? "Creating..." : t("create", { defaultValue: "Create" })}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {isEditModalOpen ? (
        <div
          className={styles.modalOverlay}
          onClick={closeEditModal}
          role="presentation"
        >
          <div
            className={styles.modalCard}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-role-modal-title"
          >
            <div className={styles.modalHeader}>
              <div>
                <h2 id="edit-role-modal-title" className={styles.modalTitle}>
                  {t("edit_role", { defaultValue: "Edit role" })}
                </h2>
                <div className={styles.modalSubtitle}>
                  {t("permissions_update_role_hint")}
                </div>
              </div>

              <button
                type="button"
                className={styles.modalCloseButton}
                onClick={closeEditModal}
                disabled={editingRole}
                aria-label={t("close")}
              >
                <i className="lni lni-xmark"></i>
              </button>
            </div>

            <div className={styles.modalBody}>
              <div className={styles.modalField}>
                <label className={styles.label}>{t("permissions_role_name")}</label>
                <input
                  autoFocus
                  className={styles.input}
                  value={editRoleName}
                  onChange={(e) => setEditRoleName(e.target.value)}
                  placeholder={t("permissions_role_name_placeholder")}
                  disabled={editingRole || !canRenameSelectedRole}
                />
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={closeEditModal}
                disabled={editingRole}
              >
                {t("cancel", { defaultValue: "Cancel" })}
              </button>

              <button
                type="button"
                className={styles.primaryButton}
                onClick={handleEditRole}
                disabled={
                  editingRole || !editRoleName.trim() || !canRenameSelectedRole
                }
                title={
                  !canRenameSelectedRole
                    ? selectedRoleIsSystem
                      ? "Cannot edit system role"
                      : "Permission denied: roles.edit"
                    : "Save"
                }
              >
                {editingRole ? "Saving..." : t("save", { defaultValue: "Save" })}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
